import { useQueryClient } from '@tanstack/react-query';
import { getCollectionListQueryKey, getCollectionLookupQueryKey, } from '../collection/collectionRepository';
import { getRecordListQueryKey } from '../record/recordRepository';
const PREFIX = ['research', 'form'];
export function createFormRepository(config) {
    const { repository, client } = config;
    function getFindQueryKey(props) {
        return [...PREFIX, 'find', props];
    }
    function getRetrieveQueryKey(props) {
        return [...PREFIX, 'retrieve', props.formId];
    }
    return {
        usePrefetchRetrieve(context) {
            const queryClient = useQueryClient();
            return {
                trigger(props) {
                    return queryClient.prefetchQuery({
                        queryKey: getRetrieveQueryKey(props),
                        async queryFn() {
                            const { data: form } = await client.user.default.formRetrieve(props);
                            return form;
                        },
                    });
                },
            };
        },
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getFindQueryKey(props),
                async queryFn() {
                    const { data: { data: forms }, } = await client.user.default.formList(props);
                    return forms;
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getRetrieveQueryKey(props),
                async queryFn() {
                    const { data: form } = await client.user.default.formRetrieve(props);
                    return form;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const response = await client.user.default.formCreate({
                        formCreatePayload: {
                            ...props,
                            fields: [
                                ...props.fields,
                                {
                                    // TODO centralize and constantize transcript key
                                    key: 'transcript',
                                    type: 'snippet',
                                    required: true,
                                    display_name: 'Transcript',
                                    description: 'Raw transcript of the interview',
                                },
                            ],
                        },
                    });
                    return response.data;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({ queryKey: PREFIX });
                    // Refetch collection cache since the backend creates/updates the collection
                    // Using refetchQueries to ensure loading indicator stays visible until all data is updated
                    await queryClient.refetchQueries({
                        queryKey: getCollectionLookupQueryKey({
                            collectionId: data.collection,
                        }),
                    });
                    await queryClient.refetchQueries({
                        queryKey: getCollectionListQueryKey({}),
                    });
                    // queryClient.setQueryData(
                    //     queryKey,
                    //     (old: generated.Form[] | undefined) => {
                    //         return [...(old ?? []), data];
                    //     }
                    // );
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn({ formId, payload }) {
                    // If updating fields, ensure default fields are preserved
                    const current = queryClient.getQueryData(getRetrieveQueryKey({ formId }));
                    let fields = payload.fields ?? [];
                    // TODO centralize and constantize transcript key
                    const transcriptKey = 'transcript';
                    const transcriptField = current?.config.fields.find((field) => field.key === transcriptKey);
                    if (payload.fields) {
                        fields = [
                            ...payload.fields,
                            {
                                id: transcriptField?.id,
                                key: transcriptKey,
                                type: 'snippet',
                                required: true,
                                display_name: 'Transcript',
                                description: 'Raw transcript of the interview',
                            },
                        ];
                    }
                    const updatePayload = {
                        ...payload,
                        fields,
                    };
                    console.log('updatePayload', updatePayload);
                    const response = await client.user.default.formUpdate({
                        formId,
                        formUpdatePayload: updatePayload,
                    });
                    return response.data;
                },
                async onMutate({ formId, payload }) {
                    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
                    await queryClient.cancelQueries({
                        queryKey: getRetrieveQueryKey({ formId }),
                    });
                    // Snapshot the previous value
                    const previousForm = queryClient.getQueryData(getRetrieveQueryKey({ formId }));
                    // Cancel collection queries to prevent overwriting optimistic updates
                    // Note: We use cancelQueries here but refetchQueries in onSuccess to ensure
                    // loading indicator stays visible until all data is consistently updated
                    if (previousForm) {
                        await queryClient.cancelQueries({
                            queryKey: getCollectionLookupQueryKey({
                                collectionId: previousForm.collection,
                            }),
                        });
                    }
                    // Optimistically update the cache
                    if (previousForm) {
                        const optimisticForm = {
                            ...previousForm,
                            title: payload.title ?? previousForm.title,
                            description: payload.description ?? previousForm.description,
                            status: payload.status ?? previousForm.status,
                            config: payload.config
                                ? {
                                    ...previousForm.config,
                                    ...payload.config,
                                }
                                : previousForm.config,
                            updated_at: new Date(),
                        };
                        queryClient.setQueryData(getRetrieveQueryKey({ formId }), optimisticForm);
                    }
                    // Return a context object with the snapshotted value
                    return { previousForm };
                },
                async onError(err, { formId }, context) {
                    // If the mutation fails, use the context returned from onMutate to roll back
                    if (context?.previousForm) {
                        queryClient.setQueryData(getRetrieveQueryKey({ formId }), context.previousForm);
                        // Refetch collection cache to ensure we get fresh data
                        await queryClient.refetchQueries({
                            queryKey: getCollectionLookupQueryKey({
                                collectionId: context.previousForm.collection,
                            }),
                        });
                        // Refetch records/documents for this collection to ensure consistency
                        await queryClient.refetchQueries({
                            queryKey: getRecordListQueryKey({
                                collection: context.previousForm.collection,
                            }),
                        });
                    }
                },
                async onSuccess(data) {
                    // Update with the actual server response
                    queryClient.setQueryData(getRetrieveQueryKey({ formId: data.id }), data);
                    // Refetch the list to ensure consistency
                    await queryClient.refetchQueries({ queryKey: PREFIX });
                    // Refetch collection cache since the backend updates the collection
                    // Using refetchQueries to ensure loading indicator stays visible until all data is updated
                    await queryClient.refetchQueries({
                        queryKey: getCollectionLookupQueryKey({
                            collectionId: data.collection,
                        }),
                    });
                    await queryClient.refetchQueries({
                        queryKey: getCollectionListQueryKey({}),
                    });
                    // Refetch records/documents for this collection to ensure new field keys are returned
                    await queryClient.refetchQueries({
                        queryKey: getRecordListQueryKey({
                            collection: data.collection,
                        }),
                    });
                },
                async onSettled(data, error, { formId }) {
                    // Always refetch after error or success to ensure we have the latest data
                    await queryClient.invalidateQueries({
                        queryKey: getRetrieveQueryKey({ formId }),
                    });
                    // Refetch collection cache to ensure consistency
                    if (data) {
                        await queryClient.refetchQueries({
                            queryKey: getCollectionLookupQueryKey({
                                collectionId: data.collection,
                            }),
                        });
                        // Refetch records/documents for this collection to ensure consistency
                        await queryClient.refetchQueries({
                            queryKey: getRecordListQueryKey({
                                collection: data.collection,
                            }),
                        });
                    }
                },
            });
        },
    };
}
//# sourceMappingURL=formRepository.js.map